import React from 'react';
import { useStowageStore } from 'src/context/StowageContext';
import OdfjellInputComponent, { InputChangeData } from 'src/components/common/odfjell-input-component/OdfjellInputComponent';
import { PanelFlexWrapper, PanelSectionContent } from 'src/components/ui/odfjellPanel/OdfjellPanel.styles';
import OdfjellPanel, { PANEL_CONSTANTS } from 'src/components/ui/odfjellPanel/OdfjellPanel';
import { resetColumnWidths } from './utils/columnWidthUtils';
import { getDefaultColumnSelections } from './utils/columnSelectionUtils';
import { ButtonWrapper, ResetColumnsButton } from './SelectColumnsPanel.styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PanelDimensions } from 'src/types/ViewModel/Stowage/PanelDimensions';
import { usePositionFromClick } from 'src/hooks/stowage/usePositionFromClick';

const SelectColumnsPanel: React.FC = () => {
    const { setIsColumnsPanelOpen, operationListColumns, setOperationListColumns, setOperationListColumnWidths, mouseClickPosition } =
        useStowageStore();

    // Define panel dimensions for position calculation
    const panelDimensions: PanelDimensions = {
        width: PANEL_CONSTANTS.MEDIUM_WIDTH,
        height: 500, // Estimate the panel height
    };

    // Use the hook to calculate optimal position
    const panelPosition = usePositionFromClick(mouseClickPosition, panelDimensions);

    const onUpdateColumns = () => {
        setIsColumnsPanelOpen(false);
    };

    const onInputChange = (changeObject: InputChangeData) => {
        const name = changeObject.event.target.name;
        const value = changeObject.event.target.checked;

        changeObject.dataObject[name] = value;

        setOperationListColumns({ ...operationListColumns });
        localStorage.setItem('operationListColumns', JSON.stringify(operationListColumns));
    };

    const onClosePanel = () => {
        setIsColumnsPanelOpen(false);
    };

    const resetColumnWidthsHandler = () => {
        // Get default widths
        const defaultWidths = resetColumnWidths();

        // Apply default widths to state
        setOperationListColumnWidths(defaultWidths);

        // Clear localStorage
        localStorage.removeItem('operationListColumnWidths');

        // Apply default widths directly to DOM elements
        document.querySelectorAll('[data-column-name]').forEach((column: Element) => {
            const columnName = column.getAttribute('data-column-name');
            if (columnName && defaultWidths[columnName]) {
                (column as HTMLElement).style.width = defaultWidths[columnName];
                // Also clear any inline flex styling that might have been applied
                (column as HTMLElement).style.flex = '';
            }
        });
    };

    const resetColumnSelectionsHandler = () => {
        // Get default column selections
        const defaultSelections = getDefaultColumnSelections();

        // Apply default selections to state
        setOperationListColumns(defaultSelections);

        // Save to localStorage
        localStorage.setItem('operationListColumns', JSON.stringify(defaultSelections));
    };

    return (
        <OdfjellPanel
            headerTitle={'Select Visible Columns'}
            defaultPosition="center"
            initialPosition={mouseClickPosition ? panelPosition : undefined}
            onCancel={onClosePanel}
            onAction={onUpdateColumns}
            actionButtonText="Close"
        >
            <PanelFlexWrapper>
                <div className="left-wrapper">
                    <PanelSectionContent>
                        <OdfjellInputComponent
                            isReadOnly={false}
                            type={'checkbox'}
                            description={'Trade name'}
                            value={operationListColumns?.showTradeName}
                            dataObject={operationListColumns}
                            name={'showTradeName'}
                            onChange={onInputChange}
                        />
                        <OdfjellInputComponent
                            isReadOnly={false}
                            type={'checkbox'}
                            description={'Product name'}
                            value={operationListColumns?.showProductName}
                            dataObject={operationListColumns}
                            name={'showProductName'}
                            onChange={onInputChange}
                        />
                        <OdfjellInputComponent
                            isReadOnly={false}
                            type={'checkbox'}
                            description={'Charterer'}
                            value={operationListColumns?.showCharterer}
                            dataObject={operationListColumns}
                            name={'showCharterer'}
                            onChange={onInputChange}
                        />
                        <OdfjellInputComponent
                            isReadOnly={false}
                            type={'checkbox'}
                            description={'Contract weight'}
                            value={operationListColumns?.showContractWeight}
                            dataObject={operationListColumns}
                            name={'showContractWeight'}
                            onChange={onInputChange}
                        />
                        <OdfjellInputComponent
                            isReadOnly={false}
                            type={'checkbox'}
                            description={'Nominated weight'}
                            value={operationListColumns?.showNominatedWeight}
                            dataObject={operationListColumns}
                            name={'showNominatedWeight'}
                            onChange={onInputChange}
                        />
                        <OdfjellInputComponent
                            isReadOnly={false}
                            type={'checkbox'}
                            description={'Total cargo nominated weight'}
                            value={operationListColumns?.showTotalCargoNominatedWeight}
                            dataObject={operationListColumns}
                            name={'showTotalCargoNominatedWeight'}
                            onChange={onInputChange}
                        />
                        <OdfjellInputComponent
                            isReadOnly={false}
                            type={'checkbox'}
                            description={'Bill of lading figure'}
                            value={operationListColumns?.showBillOfLadingFigure}
                            dataObject={operationListColumns}
                            name={'showBillOfLadingFigure'}
                            onChange={onInputChange}
                        />
                        <OdfjellInputComponent
                            isReadOnly={false}
                            type={'checkbox'}
                            description={'Ship figure'}
                            value={operationListColumns?.showShipFigure}
                            dataObject={operationListColumns}
                            name={'showShipFigure'}
                            onChange={onInputChange}
                        />
                        <OdfjellInputComponent
                            isReadOnly={false}
                            type={'checkbox'}
                            description={'Total cargo nominated/swing weights'}
                            value={operationListColumns?.showSwingWeights}
                            dataObject={operationListColumns}
                            name={'showSwingWeights'}
                            onChange={onInputChange}
                        />
                        <OdfjellInputComponent
                            isReadOnly={false}
                            type={'checkbox'}
                            description={'Total cargo nominated/swing volumes'}
                            value={operationListColumns?.showSwingVolumes}
                            dataObject={operationListColumns}
                            name={'showSwingVolumes'}
                            onChange={onInputChange}
                        />
                        <OdfjellInputComponent
                            isReadOnly={false}
                            type={'checkbox'}
                            description={'Nominated volume'}
                            value={operationListColumns?.showNominatedVolume}
                            dataObject={operationListColumns}
                            name={'showNominatedVolume'}
                            onChange={onInputChange}
                        />
                        <OdfjellInputComponent
                            isReadOnly={false}
                            type={'checkbox'}
                            description={'Stowed weight'}
                            value={operationListColumns?.showStowedWeight}
                            dataObject={operationListColumns}
                            name={'showStowedWeight'}
                            onChange={onInputChange}
                        />
                        <OdfjellInputComponent
                            isReadOnly={false}
                            type={'checkbox'}
                            description={'Stowed volume'}
                            value={operationListColumns?.showStowedVolume}
                            dataObject={operationListColumns}
                            name={'showStowedVolume'}
                            onChange={onInputChange}
                        />
                        <OdfjellInputComponent
                            isReadOnly={false}
                            type={'checkbox'}
                            description={'Not stowed weight'}
                            value={operationListColumns?.showNotStowedWeight}
                            dataObject={operationListColumns}
                            name={'showNotStowedWeight'}
                            onChange={onInputChange}
                        />
                        <OdfjellInputComponent
                            isReadOnly={false}
                            type={'checkbox'}
                            description={'Not stowed volume'}
                            value={operationListColumns?.showNotStowedVolume}
                            dataObject={operationListColumns}
                            name={'showNotStowedVolume'}
                            onChange={onInputChange}
                        />
                        <OdfjellInputComponent
                            isReadOnly={false}
                            type={'checkbox'}
                            description={'Option'}
                            value={operationListColumns?.showOption}
                            dataObject={operationListColumns}
                            name={'showOption'}
                            onChange={onInputChange}
                        />
                        <ButtonWrapper>
                            <ResetColumnsButton onClick={resetColumnWidthsHandler}>
                                Reset Column Widths
                                <FontAwesomeIcon className="icon" icon="arrows-alt-h" />
                            </ResetColumnsButton>
                            <ResetColumnsButton onClick={resetColumnSelectionsHandler}>
                                Reset to Default Columns
                                <FontAwesomeIcon className="icon" icon="undo" />
                            </ResetColumnsButton>
                        </ButtonWrapper>
                    </PanelSectionContent>
                </div>
                <div className="right-wrapper">
                    <PanelSectionContent>
                        <OdfjellInputComponent
                            isReadOnly={false}
                            type={'checkbox'}
                            description={'Litre weight'}
                            value={operationListColumns?.showLitreWeight}
                            dataObject={operationListColumns}
                            name={'showLitreWeight'}
                            onChange={onInputChange}
                        />
                        <OdfjellInputComponent
                            isReadOnly={false}
                            type={'checkbox'}
                            description={'Load - Unload port'}
                            value={operationListColumns?.showPorts}
                            dataObject={operationListColumns}
                            name={'showPorts'}
                            onChange={onInputChange}
                        />
                        <OdfjellInputComponent
                            isReadOnly={false}
                            type={'checkbox'}
                            description={'USCG #'}
                            value={operationListColumns?.showUscgGroupNumber}
                            dataObject={operationListColumns}
                            name={'showUscgGroupNumber'}
                            onChange={onInputChange}
                        />
                        <OdfjellInputComponent
                            isReadOnly={false}
                            type={'checkbox'}
                            description={'Pollution'}
                            value={operationListColumns?.showPollution}
                            dataObject={operationListColumns}
                            name={'showPollution'}
                            onChange={onInputChange}
                        />
                        <OdfjellInputComponent
                            isReadOnly={false}
                            type={'checkbox'}
                            description={'IMO ship type'}
                            value={operationListColumns?.showShipType}
                            dataObject={operationListColumns}
                            name={'showShipType'}
                            onChange={onInputChange}
                        />
                        <OdfjellInputComponent
                            isReadOnly={false}
                            type={'checkbox'}
                            description={'Heating'}
                            value={operationListColumns?.showHeating}
                            dataObject={operationListColumns}
                            name={'showHeating'}
                            onChange={onInputChange}
                        />
                        <OdfjellInputComponent
                            isReadOnly={false}
                            type={'checkbox'}
                            description={'Maximum adjacent temperature'}
                            value={operationListColumns?.showMaxAdjacentTemp}
                            dataObject={operationListColumns}
                            name={'showMaxAdjacentTemp'}
                            onChange={onInputChange}
                        />
                        <OdfjellInputComponent
                            isReadOnly={false}
                            type={'checkbox'}
                            description={'Flash point'}
                            value={operationListColumns?.showFlashPoint}
                            dataObject={operationListColumns}
                            name={'showFlashPoint'}
                            onChange={onInputChange}
                        />
                        <OdfjellInputComponent
                            isReadOnly={false}
                            type={'checkbox'}
                            description={'Melting point'}
                            value={operationListColumns?.showMeltingPoint}
                            dataObject={operationListColumns}
                            name={'showMeltingPoint'}
                            onChange={onInputChange}
                        />
                        <OdfjellInputComponent
                            isReadOnly={false}
                            type={'checkbox'}
                            description={'Boiling point'}
                            value={operationListColumns?.showBoilingPoint}
                            dataObject={operationListColumns}
                            name={'showBoilingPoint'}
                            onChange={onInputChange}
                        />
                        <OdfjellInputComponent
                            isReadOnly={false}
                            type={'checkbox'}
                            description={'Source'}
                            value={operationListColumns?.showSource}
                            dataObject={operationListColumns}
                            name={'showSource'}
                            onChange={onInputChange}
                        />
                        <OdfjellInputComponent
                            isReadOnly={false}
                            type={'checkbox'}
                            description={'Contract status'}
                            value={operationListColumns?.showContractStatus}
                            dataObject={operationListColumns}
                            name={'showContractStatus'}
                            onChange={onInputChange}
                        />
                        <OdfjellInputComponent
                            isReadOnly={false}
                            type={'checkbox'}
                            description={'Tanks'}
                            value={operationListColumns?.showTankNames}
                            dataObject={operationListColumns}
                            name={'showTankNames'}
                            onChange={onInputChange}
                        />
                    </PanelSectionContent>
                </div>
            </PanelFlexWrapper>
        </OdfjellPanel>
    );
};

export default SelectColumnsPanel;
